// import components
import { Icon } from '@sdb/planning-ui';
import { StyledBoxFullWidth } from 'components/shared/Box/Box.styles';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import {
  StyledNoMessagesContainer,
  StyledNoMessagesTypography
} from './NoteList.styles';

// import hooks
import { useState } from 'react';
import useRemoveEmployeeNote from '../hooks/useRemoveEmployeeNotes';

// import types
import { NoteListProps } from './NoteList.types';

// import utils
import { t } from 'i18next';
import { NotesTypeEnums } from '../Notes.types';
import NoteSlider from './NoteSlider';
import useRemoveRequestNote from 'components/flex-portal/FlexHistory/hooks/useRemoveRequestNote';

const NoteList = ({
  notes,
  isFetching,
  type,
  isEmployeePortal = false,
  isSavedByParent = false,
  onRemoveNote,
  noteVariant,
  isCompact = false,
  showTooltip = false,
  isWebSocketEnabled = false
}: NoteListProps) => {
  const { mutateAsync: removeNoteMutation } = useRemoveEmployeeNote();
  const { mutateAsync: removeRequestNoteMutation } = useRemoveRequestNote({
    isWebSocketEnabled
  });
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleDeleteNote = () => {
    if (selectedNoteId == null) return;

    if (isSavedByParent && onRemoveNote) {
      onRemoveNote(selectedNoteId);
    }

    if (type === NotesTypeEnums.FLEX_MATCH_REQUEST) {
      removeRequestNoteMutation(selectedNoteId);
    }

    if (
      (type === NotesTypeEnums.EMPLOYEE_NOTE ||
        type === NotesTypeEnums.DUTY_LINE) &&
      !isSavedByParent
    ) {
      removeNoteMutation({ noteId: selectedNoteId, type });
    }

    setSelectedNoteId(null);
  };

  const handleOpenDialog = (noteId) => {
    setSelectedNoteId(noteId);
    setDialogOpen(true);
  };

  const handleCloseDialog = (isConfirmed) => {
    setDialogOpen(false);
    if (isConfirmed) {
      handleDeleteNote();
    } else {
      setSelectedNoteId(null);
    }
  };

  return (
    <>
      {isDialogOpen && (
        <ConfirmationDialog
          title={t('deleteNote')}
          type="warning"
          handleAction={handleCloseDialog}
          cancelBtnText={t('cancel')}
          confirmBtnText={t('delete')}
        >
          <p>{t('confirmDeleteNote')}</p>
        </ConfirmationDialog>
      )}
      {isFetching ? (
        <LoadingOverlay loading={isFetching} />
      ) : notes?.length === 0 ? (
        <StyledNoMessagesContainer isCompact={isCompact}>
          <Icon
            type="inbox"
            fontSize={isCompact ? 'small' : 'large'}
            color="primary"
          />
          <StyledNoMessagesTypography
            variant={isCompact ? 'body1' : 'h6'}
            isCompact={isCompact}
          >
            {t('noNotesMessage')}
          </StyledNoMessagesTypography>
        </StyledNoMessagesContainer>
      ) : (
        <StyledBoxFullWidth>
          <NoteSlider
            notes={notes}
            isFetching={
              type === NotesTypeEnums.EMPLOYEE_NOTE ? isFetching : false
            }
            onDelete={handleOpenDialog}
            deletingId={selectedNoteId}
            isEmployeePortal={isEmployeePortal}
            noteVariant={noteVariant}
            isCompact={isCompact}
            showTooltip={showTooltip}
          />
        </StyledBoxFullWidth>
      )}
    </>
  );
};

export default NoteList;
